/**
 * BroadcastComposer — admin/manager broadcast form with audience picker.
 *
 * Audience options:
 *   - Everyone
 *   - By role (admin / manager / rep)
 *   - By district (free-text comma list)
 *   - Specific users (comma list of emails)
 *
 * Category, priority, and URL are picker inputs.  Live preview card mirrors
 * the inbox row visual.
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, Sparkle } from '@phosphor-icons/react';
import * as Phosphor from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = [
  { v: 'low',    label: 'Low',    desc: 'Inbox only · no ding' },
  { v: 'normal', label: 'Normal', desc: 'Standard delivery' },
  { v: 'high',   label: 'High',   desc: 'Toast + push when offline' },
];

const AUDIENCE_PRESETS = [
  { id: 'everyone', label: 'Everyone', desc: 'All active users' },
  { id: 'managers', label: 'Managers + Admins', desc: 'Leadership only' },
  { id: 'reps',     label: 'Reps',     desc: 'Rep accounts only' },
  { id: 'custom',   label: 'Custom',   desc: 'Pick users / districts' },
];

const BroadcastComposer = ({ categories }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [category, setCategory] = useState('broadcast');
  const [priority, setPriority] = useState('normal');
  const [audience, setAudience] = useState('everyone');
  const [customUsers, setCustomUsers] = useState('');
  const [customDistricts, setCustomDistricts] = useState('');
  const [sending, setSending] = useState(false);

  const meta = categories?.[category] || { label: 'Broadcast', icon: 'Sparkle', accent: '#A855F7' };
  const PreviewIcon = Phosphor[meta.icon] || Sparkle;

  const targets = useMemo(() => {
    if (audience === 'everyone') return { everyone: true };
    if (audience === 'managers') return { roles: ['admin', 'manager'] };
    if (audience === 'reps')     return { roles: ['rep'] };
    // custom
    const users = customUsers.split(',').map(s => s.trim()).filter(Boolean);
    const districts = customDistricts.split(',').map(s => s.trim()).filter(Boolean);
    const out = {};
    if (users.length)     out.users = users;
    if (districts.length) out.districts = districts;
    if (!users.length && !districts.length) return { everyone: true };
    return out;
  }, [audience, customUsers, customDistricts]);

  const send = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/notifications/dispatch`, {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || '/',
        category,
        priority,
        targets,
      }, { withCredentials: true });
      toast.success(`Sent to ${res.data.sent} ${res.data.sent === 1 ? 'recipient' : 'recipients'}`);
      setTitle(''); setBody('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const Field = ({ label, children }) => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[1.5px] mb-1.5 block pl-1"
        style={{ color: 'var(--cs-text-dim)' }}>{label}</label>
      {children}
    </div>
  );

  const inputCls = 'w-full px-3 py-2.5 rounded-xl text-sm bg-transparent focus:outline-none transition-all';
  const inputStyle = { border: '1px solid var(--cs-border)', color: 'var(--cs-text)' };

  return (
    <div className="space-y-4" data-testid="broadcast-composer">
      <Field label="Title">
        <input data-testid="composer-title" maxLength={140} className={inputCls} style={inputStyle}
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Big push today — everyone's in" />
      </Field>

      <Field label="Message">
        <textarea data-testid="composer-body" maxLength={400} rows={3}
          className={`${inputCls} resize-none`} style={inputStyle}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Short and motivating — full text shows in the inbox" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select className={inputCls} style={inputStyle}
            value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="composer-category">
            {Object.entries(categories || {}).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select className={inputCls} style={inputStyle}
            value={priority} onChange={(e) => setPriority(e.target.value)}
            data-testid="composer-priority">
            {PRIORITIES.map(p => <option key={p.v} value={p.v}>{p.label} — {p.desc}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Click-through URL">
        <input className={inputCls} style={inputStyle}
          value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/" data-testid="composer-url" />
      </Field>

      <Field label="Audience">
        <div className="grid grid-cols-2 gap-2">
          {AUDIENCE_PRESETS.map(a => (
            <button key={a.id} type="button"
              onClick={() => setAudience(a.id)}
              data-testid={`composer-audience-${a.id}`}
              className="px-3 py-2 rounded-xl text-left transition-all"
              style={audience === a.id
                ? { background: 'rgba(168,85,247,0.18)', border: '1px solid rgba(168,85,247,0.5)' }
                : { background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)' }}>
              <div className="text-[12px] font-semibold" style={{ color: 'var(--cs-text)' }}>{a.label}</div>
              <div className="text-[10px]" style={{ color: 'var(--cs-text-muted)' }}>{a.desc}</div>
            </button>
          ))}
        </div>
        {audience === 'custom' && (
          <div className="mt-3 space-y-2">
            <input className={inputCls} style={inputStyle}
              placeholder="user1@x.com, user2@x.com"
              value={customUsers} onChange={(e) => setCustomUsers(e.target.value)}
              data-testid="composer-custom-users" />
            <input className={inputCls} style={inputStyle}
              placeholder="District 1, District 5"
              value={customDistricts} onChange={(e) => setCustomDistricts(e.target.value)}
              data-testid="composer-custom-districts" />
          </div>
        )}
      </Field>

      {/* Preview */}
      {(title || body) && (
        <div className="rounded-xl p-3 flex items-start gap-3"
          style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}44` }}>
            <PreviewIcon size={14} weight="fill" style={{ color: meta.accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5"
              style={{ color: meta.accent }}>{meta.label} · Preview</div>
            <div className="text-[13px] font-semibold line-clamp-1" style={{ color: 'var(--cs-text)' }}>
              {title || 'Untitled'}
            </div>
            {body && (
              <div className="text-[11.5px] line-clamp-2 mt-0.5" style={{ color: 'var(--cs-text-muted)' }}>{body}</div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={send}
        disabled={sending || !title.trim()}
        data-testid="composer-send"
        className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
        style={{
          background: sending ? 'var(--cs-bg-hover)' : 'linear-gradient(135deg, #CD040B 0%, #F59E0B 100%)',
          color: '#FFF',
          opacity: !title.trim() ? 0.5 : 1,
          boxShadow: '0 8px 24px var(--cs-notif-glow)',
        }}>
        <PaperPlaneTilt size={16} weight="fill" />
        {sending ? 'Sending…' : 'Send broadcast'}
      </button>
    </div>
  );
};

export default BroadcastComposer;
